import type { AnswerOrDraft } from 'types/answers'

type AnswerLike = AnswerOrDraft & Record<string, unknown>

type ViewerNeutralAnswerUpdateDependencies = {
    getCachedAnswer: (answerId: string) => AnswerOrDraft | null | undefined
    cacheAnswer: (answer: AnswerOrDraft) => unknown
    refreshAnswer: (answerId: string) => Promise<unknown>
}

function embedId(embed: unknown): unknown {
    if (embed && typeof embed === 'object' && 'id' in embed) return embed.id
    return embed
}

export function answerHasPrivateEmbedPlaceholder(answer: unknown): boolean {
    if (!answer || typeof answer !== 'object' || !('embeds' in answer)) return false

    const embeds = answer.embeds
    return (
        Array.isArray(embeds) &&
        embeds.some(embed => String(embedId(embed) || '') === '__PRIVATE__')
    )
}

export function preserveViewerResolvedEmbeds(
    incomingAnswer: AnswerOrDraft,
    cachedAnswer: AnswerOrDraft | null | undefined,
): AnswerOrDraft {
    if (!answerHasPrivateEmbedPlaceholder(incomingAnswer)) return incomingAnswer
    if (!Array.isArray(cachedAnswer?.embeds) || cachedAnswer.embeds.length === 0) {
        return incomingAnswer
    }

    let didPreserveEmbed = false
    const embeds = incomingAnswer.embeds.map((incomingEmbed, index) => {
        if (String(embedId(incomingEmbed) || '') !== '__PRIVATE__') return incomingEmbed

        const cachedEmbed = cachedAnswer.embeds[index]
        if (!cachedEmbed || String(embedId(cachedEmbed) || '') === '__PRIVATE__') {
            return incomingEmbed
        }

        didPreserveEmbed = true
        return cachedEmbed
    })

    if (!didPreserveEmbed) return incomingAnswer

    return {
        ...(incomingAnswer as AnswerLike),
        embeds,
    } as AnswerOrDraft
}

/**
 * Shared AnswerChannel payloads are serialized without a viewer. Preserve any
 * viewer-resolved embeds already on screen, then refresh the container answer
 * through the authenticated API before accepting a private placeholder.
 */
export async function cacheViewerNeutralAnswerUpdate(
    incomingAnswer: AnswerOrDraft,
    { getCachedAnswer, cacheAnswer, refreshAnswer }: ViewerNeutralAnswerUpdateDependencies,
) {
    const answerId = String(incomingAnswer?.id || '')
    if (!answerId) return

    const needsViewerRefresh = answerHasPrivateEmbedPlaceholder(incomingAnswer)
    const answerToCache = preserveViewerResolvedEmbeds(incomingAnswer, getCachedAnswer(answerId))
    cacheAnswer(answerToCache)

    if (!needsViewerRefresh) return

    try {
        await refreshAnswer(answerId)
    } catch {
        // Keep the last viewer-resolved relationship on transient API failures.
    }
}
